import React, { useState, useEffect } from 'react';
import { Mail, Plus, Send, BarChart3, Trash2, Bot, Eye, Users, Building2, Flame, UserCheck, Search, Loader2, Image as ImageIcon, X, Save, BookOpen, Globe, Edit } from 'lucide-react';
import api from '../../services/api';

export default function EmailMarketing() {
  const [activeTab, setActiveTab] = useState('campanhas');
  const [campanhas, setCampanhas] = useState([]);
  const [remetentes, setRemetentes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [conhecimento, setConhecimento] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRemetenteModal, setShowRemetenteModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showConhecimentoModal, setShowConhecimentoModal] = useState(false);
  
  const [briefing, setBriefing] = useState('');
  const [imageIA, setImageIA] = useState(null);
  const [generatingIA, setGeneratingIA] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const [formData, setForm] = useState({
    nome: '',
    assunto: '',
    html: '',
    remetente_id: '',
    audiencia_tipo: 'leads',
    audiencia_filtros: { empreendimento_id: '' }
  });

  const [remetenteForm, setRemetenteForm] = useState({
    nome: '', email: '', smtp_host: '', smtp_port: 587,
    smtp_user: '', smtp_pass: '', smtp_secure: false, padrao: false
  });

  const [templateForm, setTemplateForm] = useState({ nome: '', assunto: '', html: '' });

  const [conhecimentoForm, setConhecimentoForm] = useState({
    empreendimento_id: '', chave: '', conteudo: '', tipo: 'texto'
  });
  const [conhecimentoArquivo, setConhecimentoArquivo] = useState(null);
  const [editingConhecimentoId, setEditingConhecimentoId] = useState(null);

  const [previewData, setPreview] = useState({ total: 0, amostra: [] });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.audiencia_tipo && (formData.audiencia_tipo !== 'leads' || formData.audiencia_filtros.empreendimento_id)) {
      fetchPreview();
    }
  }, [formData.audiencia_tipo, formData.audiencia_filtros.empreendimento_id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, r, e, t, con] = await Promise.all([
        api.get('/email-marketing/campanhas'),
        api.get('/email-marketing/remetentes'),
        api.get('/empreendimentos'),
        api.get('/email-marketing/templates'),
        api.get('/email-marketing/conhecimento')
      ]);
      setCampanhas(c.data.data);
      setRemetentes(r.data.data);
      setEmpreendimentos(e.data.data);
      setTemplates(t.data.data || []);
      setConhecimento(con.data.data || []);
      if (r.data.data.length > 0) {
        setForm(f => ({ ...f, remetente_id: r.data.data.find(x => x.padrao)?.id || r.data.data[0].id }));
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const res = await api.get('/email-marketing/audiencias/preview', {
        params: { tipo: formData.audiencia_tipo, filtros: JSON.stringify(formData.audiencia_filtros) }
      });
      setPreview(res.data);
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  const handleSaveAsTemplate = async () => {
    const templateName = formData.nome || prompt('Dê um nome para este template:', briefing.substring(0, 30));
    if (!formData.html || !templateName) return alert('Dê um nome e preencha o HTML para salvar');
    
    setSavingTemplate(true);
    try {
      await api.post('/email-marketing/templates', {
        nome: templateName + ' (Modelo)',
        assunto: formData.assunto,
        html: formData.html
      });
      const res = await api.get('/email-marketing/templates');
      setTemplates(res.data.data || []);
      alert('Template salvo com sucesso na biblioteca!');
    } catch (err) {
      alert('Erro ao salvar template: ' + err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImageIA(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateIA = async () => {
    if (!briefing) return alert('Digite um briefing para a IA');
    setGeneratingIA(true);
    try {
      const res = await api.post('/email-marketing/ia/gerar', { 
        briefing, 
        imagem_base64: imageIA,
        tipo_audiencia: formData.audiencia_tipo,
        empreendimento_id: formData.audiencia_filtros.empreendimento_id
      });
      setForm(f => ({ ...f, html: res.data.data.html, assunto: res.data.data.assunto }));
    } catch (err) {
      alert('Erro ao gerar com IA: ' + err.message);
    } finally {
      setGeneratingIA(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/email-marketing/campanhas', formData);
      setShowModal(false);
      fetchData();
      alert('Campanha criada com sucesso!');
    } catch (err) {
      alert('Erro ao criar campanha: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemetenteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/email-marketing/remetentes', remetenteForm);
      setShowRemetenteModal(false);
      fetchData();
      alert('Remetente configurado!');
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/email-marketing/templates', templateForm);
      setShowTemplateModal(false);
      fetchData();
      alert('Template salvo!');
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConhecimentoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      data.append('chave', conhecimentoForm.chave);
      data.append('tipo', conhecimentoForm.tipo);
      data.append('conteudo', conhecimentoForm.conteudo);
      if (conhecimentoForm.empreendimento_id) data.append('empreendimento_id', conhecimentoForm.empreendimento_id);
      if (conhecimentoArquivo) data.append('arquivo', conhecimentoArquivo);

      if (editingConhecimentoId) {
        await api.put(`/email-marketing/conhecimento/${editingConhecimentoId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/email-marketing/conhecimento', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowConhecimentoModal(false);
      setEditingConhecimentoId(null);
      setConhecimentoArquivo(null);
      fetchData();
      alert('Ativo salvo!');
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditConhecimento = (item) => {
    setConhecimentoForm({
      empreendimento_id: item.empreendimento_id || '',
      chave: item.chave,
      conteudo: item.conteudo,
      tipo: item.tipo
    });
    setEditingConhecimentoId(item.id);
    setShowConhecimentoModal(true);
  };

  const handleDeleteConhecimento = async (id) => {
    if (!confirm('Deseja excluir este ativo?')) return;
    try {
      await api.delete(`/email-marketing/conhecimento/${id}`);
      fetchData();
    } catch (err) {
      alert('Erro: ' + err.message);
    }
  };

  const handleDisparar = async (id) => {
    if (!confirm('Deseja iniciar o disparo desta campanha?')) return;
    try {
      await api.post(`/email-marketing/campanhas/${id}/disparar`);
      fetchData();
      alert('Disparo iniciado!');
    } catch (err) {
      alert('Erro: ' + err.message);
    }
  };

  const renderStatus = (status) => {
    const map = {
      rascunho: 'bg-gray-100 text-gray-600',
      enviando: 'bg-blue-100 text-blue-600 animate-pulse',
      enviada: 'bg-green-100 text-green-600',
      erro: 'bg-red-100 text-red-600'
    };
    return <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${map[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Mail className="text-secondary" /> Email Marketing
          </h1>
          <p className="text-gray-500 text-sm">Gerencie suas campanhas e automações de email.</p>
        </div>
        <button 
          onClick={() => {
            if (activeTab === 'campanhas') setShowModal(true);
            if (activeTab === 'remetentes') setShowRemetenteModal(true);
            if (activeTab === 'templates') setShowTemplateModal(true);
            if (activeTab === 'conhecimento') {
              setEditingConhecimentoId(null);
              setConhecimentoForm({ empreendimento_id: '', chave: '', conteudo: '', tipo: 'texto' });
              setShowConhecimentoModal(true);
            }
          }}
          className="bg-secondary hover:bg-secondary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-md"
        >
          <Plus size={18} /> 
          {activeTab === 'campanhas' && 'Nova Campanha'}
          {activeTab === 'remetentes' && 'Novo Remetente'}
          {activeTab === 'templates' && 'Novo Template'}
          {activeTab === 'conhecimento' && 'Novo Ativo'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button 
          onClick={() => setActiveTab('campanhas')}
          className={`px-4 py-2 font-medium text-sm transition-all border-b-2 ${activeTab === 'campanhas' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Campanhas
        </button>
        <button 
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 font-medium text-sm transition-all border-b-2 ${activeTab === 'templates' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Templates
        </button>
        <button 
          onClick={() => setActiveTab('conhecimento')}
          className={`px-4 py-2 font-medium text-sm transition-all border-b-2 ${activeTab === 'conhecimento' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Conhecimento
        </button>
        <button 
          onClick={() => setActiveTab('remetentes')}
          className={`px-4 py-2 font-medium text-sm transition-all border-b-2 ${activeTab === 'remetentes' ? 'border-secondary text-secondary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Remetentes SMTP
        </button>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'campanhas' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Campanha</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Audiência</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Métricas</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campanhas.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-800">{c.nome}</div>
                      <div className="text-xs text-gray-400">{c.assunto}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 flex items-center gap-1 font-medium capitalize">
                        {c.audiencia_tipo.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">{renderStatus(c.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-4 text-xs">
                        <div className="text-center">
                          <div className="font-bold text-gray-700">{c.total_enviados || 0}</div>
                          <div className="text-gray-400 font-medium">Enviados</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-blue-600">{c.total_abertos || 0}</div>
                          <div className="text-gray-400 font-medium">Abertos</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-green-600">{c.total_cliques || 0}</div>
                          <div className="text-gray-400 font-medium">Cliques</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {c.status === 'rascunho' && (
                          <button 
                            onClick={() => handleDisparar(c.id)}
                            className="p-2 text-secondary hover:bg-blue-50 rounded-lg transition-colors" title="Disparar"
                          >
                            <Send size={18} />
                          </button>
                        )}
                        <button className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                          <BarChart3 size={18} />
                        </button>
                        <button className="p-2 text-red-400 hover:bg-red-50 rounded-lg">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {campanhas.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-gray-400 font-medium">
                      Nenhuma campanha encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'templates' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(t => (
                <div key={t.id} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <div className="bg-blue-50 p-2 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setPreviewHtml(t.html)}>
                      <Eye className="text-secondary" size={20} />
                    </div>
                    <div className="flex gap-2">
                      <button className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-1">{t.nome}</h3>
                  <p className="text-xs text-gray-500 line-clamp-1 mb-4">{t.assunto}</p>
                  <button 
                    onClick={() => {
                      setForm({ ...formData, html: t.html, assunto: t.assunto || '', nome: t.nome + ' (Cópia)' });
                      setActiveTab('campanhas');
                      setShowModal(true);
                    }}
                    className="w-full py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-bold hover:bg-gray-100 transition-colors"
                  >
                    Usar Template
                  </button>
                </div>
              ))}
            </div>
            {templates.length === 0 && (
              <div className="bg-white p-12 text-center rounded-xl border border-dashed border-gray-200">
                <Mail className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="text-gray-500 font-bold">Nenhum template salvo</h3>
                <p className="text-gray-400 text-sm">Crie templates para reutilizar seus melhores designs.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'conhecimento' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Ativo</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Empreendimento</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Conteúdo</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {conhecimento.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-800 text-sm">{item.chave}</td>
                    <td className="px-6 py-4">
                      <div className="text-xs flex items-center gap-1 text-gray-500 font-medium">
                        {item.empreendimento_nome ? <Building2 size={12}/> : <Globe size={12}/>}
                        {item.empreendimento_nome || 'GERAL (CONSTRUTORA)'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${item.tipo === 'imagem' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {item.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{item.conteudo}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleEditConhecimento(item)} className="p-2 text-blue-400 hover:bg-blue-50 rounded-lg">
                          <Edit size={18} />
                        </button>
                        <button onClick={() => handleDeleteConhecimento(item.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'remetentes' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Remetente</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Configuração</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Padrão</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {remetentes.map(r => (
                  <tr key={r.id}>
                    <td className="px-6 py-4 font-semibold text-gray-800">{r.nome}<br/><span className="text-xs font-normal text-gray-400">{r.email}</span></td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">{r.smtp_host}:{r.smtp_port}</td>
                    <td className="px-6 py-4">
                      {r.padrao ? <span className="bg-blue-100 text-secondary px-2 py-0.5 rounded text-[10px] font-black uppercase">PADRÃO</span> : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg mr-2"><Search size={18}/></button>
                      <button className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={18}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Nova Campanha */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-800">Criar Nova Campanha</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Coluna 1: Dados e Audiência */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Nome da Campanha</label>
                    <input 
                      type="text" required
                      className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent outline-none"
                      placeholder="Ex: Lançamento Residencial Park"
                      value={formData.nome}
                      onChange={e => setForm({...formData, nome: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Assunto do Email</label>
                    <input 
                      type="text" required
                      className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent outline-none"
                      placeholder="Assunto que aparecerá para o cliente"
                      value={formData.assunto}
                      onChange={e => setForm({...formData, assunto: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Remetente</label>
                      <select 
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                        value={formData.remetente_id}
                        onChange={e => setForm({...formData, remetente_id: e.target.value})}
                      >
                        {remetentes.map(r => <option key={r.id} value={r.id}>{r.nome} ({r.email})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Base de Dados</label>
                      <select 
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                        value={formData.audiencia_tipo}
                        onChange={e => setForm({...formData, audiencia_tipo: e.target.value})}
                      >
                        <option value="leads">Leads por Empreendimento</option>
                        <option value="clientes">Todos os Clientes</option>
                        <option value="corretores">Todos os Corretores</option>
                        <option value="leads_reaquecidos">Leads Reaquecidos (Frios)</option>
                        <option value="compradores_empreendimento">Compradores do Empreendimento</option>
                      </select>
                    </div>
                  </div>

                  {(formData.audiencia_tipo === 'leads' || formData.audiencia_tipo === 'compradores_empreendimento') && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Selecionar Empreendimento</label>
                      <select 
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                        value={formData.audiencia_filtros.empreendimento_id}
                        onChange={e => setForm({...formData, audiencia_filtros: { ...formData.audiencia_filtros, empreendimento_id: e.target.value }})}
                      >
                        <option value="">Selecione...</option>
                        {empreendimentos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Audience Preview Card */}
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-blue-800 flex items-center gap-1"><Users size={16}/> Resumo da Audiência</span>
                      <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs font-bold">{previewData.total} destinatários</span>
                    </div>
                    <div className="space-y-1">
                      {previewData.amostra?.map(a => (
                        <div key={a.id} className="text-[11px] text-blue-600 truncate">{a.nome} ({a.email})</div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Coluna 2: Conteúdo e IA */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        Conteúdo HTML
                        {formData.html && (
                          <button 
                            type="button"
                            onClick={() => setPreviewHtml(formData.html)}
                            className="text-secondary hover:text-secondary-dark transition-colors"
                            title="Visualizar Email"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <select 
                          className="text-[10px] bg-gray-50 border-none outline-none font-bold text-secondary cursor-pointer"
                          onChange={e => {
                            const t = templates.find(x => x.id === e.target.value);
                            if (t) setForm({ ...formData, html: t.html, assunto: t.assunto || formData.assunto });
                          }}
                          value=""
                        >
                          <option value="">REUTILIZAR TEMPLATE...</option>
                          {templates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                        </select>
                        <span className="text-[10px] text-gray-400">Use {'{{nome}}'} para personalizar</span>
                      </div>
                    </label>
                    <textarea 
                      required
                      className="w-full h-48 p-3 border border-gray-200 rounded-lg font-mono text-xs focus:ring-2 focus:ring-secondary outline-none"
                      placeholder="<html>...</html>"
                      value={formData.html}
                      onChange={e => setForm({...formData, html: e.target.value})}
                    />
                    {formData.html && (
                      <button 
                        type="button"
                        onClick={handleSaveAsTemplate}
                        disabled={savingTemplate}
                        className="flex items-center gap-2 text-[10px] font-bold text-gray-500 hover:text-secondary transition-colors mt-1 uppercase"
                      >
                        {savingTemplate ? <Loader2 className="animate-spin" size={12}/> : <Save size={12}/>}
                        Salvar este design como modelo (Template)
                      </button>
                    )}
                  </div>

                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                    <label className="block text-sm font-bold text-purple-800 mb-2 flex items-center gap-1">
                      <Bot size={16} /> Gerar com IA (Briefing + Imagem)
                    </label>
                    
                    <div className="flex flex-col gap-3">
                      <textarea 
                        className="w-full p-2 text-sm border border-purple-200 rounded-lg outline-none min-h-[60px]"
                        placeholder="Ex: Convite para evento de café da manhã no Ingarden..."
                        value={briefing}
                        onChange={e => setBriefing(e.target.value)}
                      />
                      
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <input 
                            type="file" accept="image/*" id="ai-image"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                          <label 
                            htmlFor="ai-image"
                            className="flex items-center justify-center gap-2 w-full p-2 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 cursor-pointer hover:bg-purple-100 transition-all text-xs font-bold"
                          >
                            <ImageIcon size={16} /> 
                            {imageIA ? 'Trocar Imagem' : 'Anexar Referência Visual'}
                          </label>
                        </div>
                        
                        <button 
                          type="button"
                          disabled={generatingIA}
                          onClick={handleGenerateIA}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-black hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-sm"
                        >
                          {generatingIA ? <Loader2 className="animate-spin" size={18}/> : 'GERAR EMAIL'}
                        </button>
                      </div>

                      {imageIA && (
                        <div className="relative w-20 h-20 border-2 border-purple-200 rounded-lg overflow-hidden group">
                          <img src={imageIA} className="w-full h-full object-cover" alt="Preview" />
                          <button 
                            onClick={() => setImageIA(null)}
                            className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button 
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 text-gray-500 font-bold hover:bg-gray-50 rounded-lg transition-all"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  disabled={loading}
                  className="px-8 py-2 bg-secondary text-white font-bold rounded-lg hover:bg-secondary-dark transition-all shadow-md flex items-center gap-2"
                >
                  {loading ? <Loader2 className="animate-spin" size={18}/> : 'Salvar Campanha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal Novo Remetente */}
      {showRemetenteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Configurar Remetente SMTP</h2>
              <button onClick={() => setShowRemetenteModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleRemetenteSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Nome Exibido</label>
                  <input type="text" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" placeholder="Ex: Inbase Construtora" 
                    value={remetenteForm.nome} onChange={e => setRemetenteForm({...remetenteForm, nome: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Email do Remetente</label>
                  <input type="email" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" placeholder="contato@empresa.com.br" 
                    value={remetenteForm.email} onChange={e => setRemetenteForm({...remetenteForm, email: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Servidor SMTP (Host)</label>
                  <input type="text" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" placeholder="smtp.titan.email" 
                    value={remetenteForm.smtp_host} onChange={e => setRemetenteForm({...remetenteForm, smtp_host: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Porta</label>
                  <input type="number" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" placeholder="587" 
                    value={remetenteForm.smtp_port} onChange={e => setRemetenteForm({...remetenteForm, smtp_port: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Usuário/Login</label>
                  <input type="text" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" 
                    value={remetenteForm.smtp_user} onChange={e => setRemetenteForm({...remetenteForm, smtp_user: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Senha SMTP</label>
                  <input type="password" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" 
                    value={remetenteForm.smtp_pass} onChange={e => setRemetenteForm({...remetenteForm, smtp_pass: e.target.value})} />
                </div>
              </div>
              <div className="flex items-center gap-4 py-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={remetenteForm.smtp_secure} onChange={e => setRemetenteForm({...remetenteForm, smtp_secure: e.target.checked})} className="w-4 h-4 rounded text-secondary" /> Usar SSL/TLS
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={remetenteForm.padrao} onChange={e => setRemetenteForm({...remetenteForm, padrao: e.target.checked})} className="w-4 h-4 rounded text-secondary" /> Definir como Padrão
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowRemetenteModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancelar</button>
                <button type="submit" disabled={loading} className="px-8 py-2 bg-secondary text-white font-bold rounded-lg hover:bg-secondary-dark transition-all">
                  {loading ? 'Salvando...' : 'Salvar Configuração'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Novo Template */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Criar Novo Template</h2>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleTemplateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Nome do Template</label>
                <input type="text" required className="w-full p-2.5 border border-gray-200 rounded-lg outline-none" placeholder="Ex: Boas-vindas Clientes" 
                  value={templateForm.nome} onChange={e => setTemplateForm({...templateForm, nome: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Assunto Padrão</label>
                <input type="text" className="w-full p-2.5 border border-gray-200 rounded-lg outline-none" placeholder="Olá {{nome}}, temos uma novidade!" 
                  value={templateForm.assunto} onChange={e => setTemplateForm({...templateForm, assunto: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Corpo HTML</label>
                <textarea required className="w-full h-80 p-3 border border-gray-200 rounded-lg font-mono text-xs outline-none" placeholder="<html>...</html>" 
                  value={templateForm.html} onChange={e => setTemplateForm({...templateForm, html: e.target.value})} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowTemplateModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancelar</button>
                <button type="submit" disabled={loading} className="px-8 py-2 bg-secondary text-white font-bold rounded-lg hover:bg-secondary-dark transition-all">
                  {loading ? 'Salvando...' : 'Salvar Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Novo Ativo (Conhecimento) */}
      {showConhecimentoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Novo Ativo de Conhecimento</h2>
              <button onClick={() => setShowConhecimentoModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleConhecimentoSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Empreendimento</label>
                  <select 
                    className="w-full p-2 border border-gray-200 rounded-lg outline-none text-sm"
                    value={conhecimentoForm.empreendimento_id}
                    onChange={e => setConhecimentoForm({...conhecimentoForm, empreendimento_id: e.target.value})}
                  >
                    <option value="">GERAL (TODA CONSTRUTORA)</option>
                    {empreendimentos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Tipo de Ativo</label>
                  <select 
                    className="w-full p-2 border border-gray-200 rounded-lg outline-none text-sm"
                    value={conhecimentoForm.tipo}
                    onChange={e => setConhecimentoForm({...conhecimentoForm, tipo: e.target.value})}
                  >
                    <option value="texto">Texto / Descrição / Endereço</option>
                    <option value="imagem">Link de Imagem / Logo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Nome do Ativo (Chave)</label>
                <input type="text" required className="w-full p-2 border border-gray-200 rounded-lg outline-none" placeholder="Ex: Logo Principal ou Endereço Obra" 
                  value={conhecimentoForm.chave} onChange={e => setConhecimentoForm({...conhecimentoForm, chave: e.target.value})} />
              </div>
              
              {conhecimentoForm.tipo === 'imagem' ? (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Upload da Imagem</label>
                  <input type="file" accept="image/*" required className="w-full p-2 border border-gray-200 rounded-lg outline-none text-sm"
                    onChange={e => setConhecimentoArquivo(e.target.files[0])} />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Conteúdo (Texto / Endereço)</label>
                  <textarea required className="w-full h-32 p-3 border border-gray-200 rounded-lg outline-none text-sm" 
                    placeholder="Digite o texto ou endereço aqui..."
                    value={conhecimentoForm.conteudo} onChange={e => setConhecimentoForm({...conhecimentoForm, conteudo: e.target.value})} />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowConhecimentoModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancelar</button>
                <button type="submit" disabled={loading} className="px-8 py-2 bg-secondary text-white font-bold rounded-lg hover:bg-secondary-dark transition-all">
                  {loading ? 'Salvando...' : 'Salvar Ativo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Preview (Iframe) */}
      {previewHtml && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="bg-secondary/10 p-1.5 rounded-lg">
                  <Eye className="text-secondary" size={18} />
                </div>
                <h3 className="font-bold text-gray-800">Visualização do Email</h3>
              </div>
              <button 
                onClick={() => setPreviewHtml(null)}
                className="bg-white hover:bg-gray-100 text-gray-500 p-2 rounded-full transition-all border border-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 bg-[#f4f4f4] p-8 overflow-auto flex justify-center">
              <div className="bg-white shadow-lg w-full max-w-[650px] min-h-full">
                <iframe 
                  title="Email Preview"
                  className="w-full h-full border-none min-h-[700px]"
                  srcDoc={previewHtml}
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 bg-white text-center text-xs text-gray-400 font-medium">
              Esta é uma representação de como o cliente verá o email. Imagens e links podem variar dependendo do provedor (Gmail, Outlook, etc).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
